/**
 * SlidesModeControls
 *
 * Navigation controls for slides presentation mode.
 * Shows prev/next arrows, slide counter, and fullscreen toggle.
 * Supports keyboard navigation (Arrow Left/Right).
 */

import { useEffect, useCallback, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SlidesModeControlsProps {
  currentSlideIndex: number;
  totalSlides: number;
  onPrevious: () => void;
  onNext: () => void;
  onFullscreen?: () => void;
  isFullscreen?: boolean;
  /** Current slide title (optional) */
  currentSlideTitle?: string;
  /** Hide the controls (for auto-hide behavior) */
  hidden?: boolean;
  /** Compact layout for narrow containers (<480px) */
  compact?: boolean;
  className?: string;
}

export function SlidesModeControls({
  currentSlideIndex,
  totalSlides,
  onPrevious,
  onNext,
  onFullscreen,
  isFullscreen,
  currentSlideTitle,
  hidden = false,
  compact = false,
  className,
}: SlidesModeControlsProps) {
  const canGoPrevious = currentSlideIndex > 0;
  const canGoNext = currentSlideIndex < totalSlides - 1;

  const [isTouchDevice] = useState(
    () => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (canGoPrevious) onPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (canGoNext) onNext();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          onFullscreen?.();
          break;
        case 'Escape':
          if (isFullscreen) {
            e.preventDefault();
            onFullscreen?.();
          }
          break;
      }
    },
    [canGoPrevious, canGoNext, onPrevious, onNext, onFullscreen, isFullscreen]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      {/* Left Arrow - Previous Slide */}
      <button
        onClick={onPrevious}
        disabled={!canGoPrevious}
        className={cn(
          'absolute top-1/2 -translate-y-1/2 z-20',
          compact ? 'left-2 h-10 w-10' : 'left-4 h-12 w-12',
          'rounded-full',
          'flex items-center justify-center',
          'bg-black/40 backdrop-blur-sm border border-white/10',
          'text-white transition-all duration-200',
          canGoPrevious
            ? 'hover:bg-black/60 hover:scale-105 cursor-pointer'
            : 'opacity-30 cursor-not-allowed',
          hidden && 'opacity-0 pointer-events-none'
        )}
        title="Previous slide (Left Arrow)"
        aria-label="Previous slide"
      >
        <ChevronLeft className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
      </button>

      {/* Right Arrow - Next Slide */}
      <button
        onClick={onNext}
        disabled={!canGoNext}
        className={cn(
          'absolute top-1/2 -translate-y-1/2 z-20',
          compact ? 'right-2 h-10 w-10' : 'right-4 h-12 w-12',
          'rounded-full',
          'flex items-center justify-center',
          'bg-black/40 backdrop-blur-sm border border-white/10',
          'text-white transition-all duration-200',
          canGoNext
            ? 'hover:bg-black/60 hover:scale-105 cursor-pointer'
            : 'opacity-30 cursor-not-allowed',
          hidden && 'opacity-0 pointer-events-none'
        )}
        title="Next slide (Right Arrow)"
        aria-label="Next slide"
      >
        <ChevronRight className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
      </button>

      {/* Bottom Controls Bar */}
      <div
        className={cn(
          'absolute left-0 right-0 z-20',
          compact ? 'bottom-3' : 'bottom-8',
          'flex justify-center',
          'transition-opacity duration-300',
          hidden && 'opacity-0 pointer-events-none',
          className
        )}
      >
        <div
          className={cn(
            'bg-black/60 backdrop-blur-xl rounded-full',
            'flex items-center',
            compact ? 'px-3 py-2 gap-2' : 'px-6 py-3 gap-4',
            'border border-white/10 shadow-2xl'
          )}
        >
          {/* Slide Counter */}
          <div className="flex items-center gap-2">
            <span className={cn('text-white font-medium', compact ? 'text-xs' : 'text-sm')}>
              {currentSlideIndex + 1}
            </span>
            <span className={cn('text-white/40', compact ? 'text-xs' : 'text-sm')}>/</span>
            <span className={cn('text-white/60', compact ? 'text-xs' : 'text-sm')}>{totalSlides}</span>
          </div>

          {/* Current Slide Title (truncated) - hidden in compact */}
          {!compact && currentSlideTitle && (
            <>
              <div className="w-px h-4 bg-white/20" />
              <span className="text-white/70 text-sm max-w-[200px] truncate">
                {currentSlideTitle}
              </span>
            </>
          )}

          {/* Navigation Hint - hidden in compact */}
          {!compact && (
            <>
              <div className="w-px h-4 bg-white/20" />
              {isTouchDevice ? (
                <span className="text-white/40 text-xs">Swipe to navigate</span>
              ) : (
                <span className="text-white/40 text-xs flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono">
                    ←
                  </kbd>
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono">
                    →
                  </kbd>
                  <span className="ml-1">to navigate</span>
                </span>
              )}
            </>
          )}

          {/* Fullscreen Toggle */}
          {onFullscreen && (
            <>
              <div className="w-px h-4 bg-white/20" />
              <button
                onClick={onFullscreen}
                className={cn(
                  'rounded-full',
                  'flex items-center justify-center',
                  'text-white/70 hover:text-white',
                  'hover:bg-white/10 transition-colors',
                  compact ? 'h-10 w-10' : 'h-8 w-8'
                )}
                title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
